import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { toast } from "react-toastify";
import { listExternalLabs } from "api/externalLabs";
import { LAB_STATUS_FILTERS, formatDate, labActions } from "utils/externalLabRules";
import ExternalLabFormDialog from "./ExternalLabFormDialog";
import ExternalLabDetailsDialog from "./ExternalLabDetailsDialog";
import ExternalLabStatusChip from "./ExternalLabStatusChip";

const PER_PAGE_OPTIONS = [10, 20, 50];

const ExternalLabList = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(null);
  const [detailsId, setDetailsId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await listExternalLabs({
      page: page + 1,
      perPage,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(status ? { status } : {}),
    });
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error || "Error while fetching external labs!");
      setRows([]);
      return;
    }
    setRows(res.data);
    setMeta(res.meta);
  }, [page, perPage, search, status]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box>
          <Typography component="h1" sx={{ fontWeight: 700, fontSize: { xs: 18, sm: 20 } }}>
            External Lab Management
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
            Register external labs, verify them, and map approved labs to hospitals / clinics.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setForm({ lab: null })}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Add Lab
        </Button>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={5}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            placeholder="Lab, owner, contact number or HFR ID"
            value={search}
            onChange={(event) => {
              setPage(0);
              setSearch(event.target.value);
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="external-lab-status">Status</InputLabel>
            <Select
              labelId="external-lab-status"
              label="Status"
              value={status}
              onChange={(event) => {
                setPage(0);
                setStatus(event.target.value);
              }}
            >
              {LAB_STATUS_FILTERS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ border: "1px solid #E5E7EB", borderRadius: 2, bgcolor: "#fff" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ py: 6, textAlign: "center", color: "#6B7280" }}>
            No external labs found.
          </Typography>
        ) : (
          <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead sx={{ background: "#F0F4FF" }}>
                <TableRow>
                  {[
                    "Lab Name",
                    "Lab Owner Name",
                    "Contact Number",
                    "Status",
                    "Mapped clinics",
                    "Registered",
                    "Actions",
                  ].map((header) => (
                    <TableCell key={header} sx={{ fontWeight: 700, fontSize: 12.5 }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((lab) => (
                  <TableRow key={lab._id} hover data-testid="external-lab-row">
                    <TableCell sx={{ fontWeight: 600 }}>{lab.labName}</TableCell>
                    <TableCell>{lab.ownerName}</TableCell>
                    <TableCell>{lab.contactNumber}</TableCell>
                    <TableCell>
                      <ExternalLabStatusChip status={lab.status} />
                    </TableCell>
                    <TableCell>{lab.activeMappingCount ?? 0}</TableCell>
                    <TableCell>{formatDate(lab.createdAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.25 }}>
                        <Tooltip title="View / verify">
                          <IconButton
                            size="small"
                            aria-label={`View ${lab.labName}`}
                            onClick={() => setDetailsId(lab._id)}
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            labActions(lab.status).canEdit
                              ? "Edit"
                              : "A rejected registration cannot be edited"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              aria-label={`Edit ${lab.labName}`}
                              disabled={!labActions(lab.status).canEdit}
                              onClick={() => setForm({ lab })}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          component="div"
          count={meta?.total || 0}
          page={page}
          onPageChange={(_, next) => setPage(next)}
          rowsPerPage={perPage}
          rowsPerPageOptions={PER_PAGE_OPTIONS}
          onRowsPerPageChange={(event) => {
            setPerPage(Number(event.target.value));
            setPage(0);
          }}
        />
      </Box>

      <ExternalLabFormDialog
        open={Boolean(form)}
        lab={form?.lab}
        onClose={(changed, message, level) => {
          setForm(null);
          if (!changed) return;
          (level === "warning" ? toast.warning : toast.success)(message || "Saved");
          load();
        }}
      />

      <ExternalLabDetailsDialog
        open={Boolean(detailsId) && !form}
        labId={detailsId}
        onClose={() => setDetailsId(null)}
        onEdit={(lab) => setForm({ lab })}
        onChanged={load}
      />
    </Box>
  );
};

export default ExternalLabList;
